import { Typography } from '@mui/material'
import { useEffect, useState } from 'react'

import { Maybe, PostCategoryDetail } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenuProps } from '@/components/layouts'
import { Tabs } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, Template } from '@/core/architecture'
import { individualDisplayResponsive } from '@/core/individual'

import { EmptyGalleryList } from './EmptyGalleryList'
import { GalleryList } from './GalleryList'

export type State = {
  categories?: Pick<
    PostCategoryDetail,
    'name' | 'id' | 'createdAt' | 'description' | 'updatedAt'
  >[]
  loading: boolean
  refetching: boolean
  artistName: string
  liveStreamId: string | undefined
  user?: Maybe<SideMenuProps['user']>
}

export type Action = {}

export const ArtistGalleryTemplate: Template<State, Action> = ({
  state: { categories, loading, artistName, liveStreamId, user },
}) => {
  const { t, i18n } = useTranslation('artistGallery')
  const { router, routerPages, routerNames, generatePath } = useRouter<{
    artistId: string
    tab: string
  }>()
  const [currentTab, setCurrentTab] = useState<number>(0)

  const handleTabChange = (value: number) => {
    setCurrentTab(value)
    router.replace(
      generatePath(routerPages.ArtistGalleryTabs, {
        artistId: router.query.artistId,
        tab: categories ? categories[value].id : '',
      })
    )
  }
  const displayResponsive = individualDisplayResponsive()

  useEffect(() => {
    const index = categories
      ? categories.findIndex(element => element.id === router.query.tab)
      : 0
    setCurrentTab(index >= 0 ? index : 0)
  }, [categories, router.query.tab])

  return (
    <ArtistThemeProvider>
      <BaseLayout
        navigation={
          config.artistTabs.includes('home')
            ? {
                type: 'artist',
                props: {
                  hasManagementTask: !!user?.hasManagementTask,
                  unreadNotificationCount: user?.unreadNotificationCount,
                  isLive: !!liveStreamId,
                },
                responsive: displayResponsive,
              }
            : undefined
        }
        header={{
          title: t('title'),
          back: true,
          defaultBackPage: generatePath(routerPages.ArtistTop, {
            artistId:
              (config.applicationType === 'standalone'
                ? config.artistId
                : router.query.artistId) ?? '#',
          }),
          ...(displayResponsive ? { responsive: true, maxWidth: 1120 } : {}),
        }}
        maxWidth={displayResponsive ? 1120 : undefined}
      >
        {/* LANAアプリで画面のタイトルを変更しているため、og:titleも合わせて変更する */}
        <Head
          title={
            i18n.exists('artistGallery:ogTitle')
              ? [t('ogTitle'), artistName]
              : [routerNames.ArtistGallery, artistName]
          }
        />
        {categories ? (
          <Tabs
            labels={categories.map(category => category.name)}
            value={currentTab}
            onChange={handleTabChange}
          >
            {categories.map(category => {
              return (
                <>
                  <Typography
                    color="primary.main"
                    sx={{ marginLeft: '12px', marginBottom: '3%' }}
                  >
                    {category.description}
                  </Typography>
                  <GalleryList
                    key={category.id}
                    categoryId={category.id}
                    user={user}
                    responsive={displayResponsive}
                  />
                </>
              )
            })}
          </Tabs>
        ) : (
          // NOTE ローディングが完了した後、カテゴリーが存在しなければ投稿なしの表示をする
          !loading && <EmptyGalleryList />
        )}
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

export const SomethingCell: Cell = () => null
